import { Tabs, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { cn } from '@shared/utils/utils';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';
import { Linked, ProductData, Stock } from '../display';
import { ProductBatches } from '../display/ProductBatches';
import { ProductDataSkeleton } from '../skeletons';

/** Contenedor de panel sin usar Radix TabsContent: evita desmontar al cambiar de tab (React 19 + portales de Select/Combobox/Dialog = removeChild). */
function ProductTabPanel({
  value,
  activeTab,
  className,
  children,
  'data-testid': dataTestId,
}: {
  value: string;
  activeTab: string;
  className?: string;
  children: React.ReactNode;
  'data-testid'?: string;
}) {
  const isActive = activeTab === value;
  return (
    <div
      role="tabpanel"
      data-state={isActive ? 'active' : 'inactive'}
      data-value={value}
      hidden={!isActive}
      tabIndex={isActive ? 0 : -1}
      className={cn(
        'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}

// Tipos para las props del componente
interface ProductTabsProps {
  // Referencias
  productDataRef: React.RefObject<any>;

  // Estados del formulario
  nombre: string;
  setNombre: (value: string) => void;
  descripcion: string;
  setDescripcion: (value: string) => void;
  codigo: string;
  setCodigo: (value: string) => void;
  coste: string;
  setCoste: (value: string) => void;
  categoria: string;
  setCategoria: (value: string) => void;
  imagenPreview: string | null;
  setImagenPreview: (value: string | null) => void;
  imageFile: File | null;
  setImageFile: (value: File | null) => void;
  length: string;
  setLength: (value: string) => void;
  heigth: string;
  setHeigth: (value: string) => void;
  width: string;
  setWidth: (value: string) => void;
  weight: string;
  setWeight: (value: string) => void;
  weightUnit: string;
  setWeightUnit: (value: string) => void;
  dimensionUnit: string;
  setDimensionUnit: (value: string) => void;
  batchActive: boolean;
  setBatchActive: (value: boolean) => void;
  batchType: string;
  setBatchType: (value: string) => void;

  // Estados de datos externos
  categorias: Array<{ id: string; name: string }>;
  loadingCategorias: boolean;
  stocks: Array<any>;
  enlaces: Array<any>;
  shops: Array<any>;
  initialData: any;

  // Funciones de manejo
  onAddStock: () => void;
  onDeleteStock: (ubicacionId: string) => void;
  onEditStock: (row: { ubicacionId?: string; codigo: string }) => void;
  // Nota: edición de stock se maneja en Main con el modal; si se requiere, se puede pasar un handler
  onCreateLink: () => void;
  onDeleteLink: (index: number) => void;

  // Utilidades
  productId?: string;
  readOnly?: boolean;
  /** Ref al ConfirmModal de desenlazar (en Main), fuera de Tabs, para no mezclar portales con Radix Tabs */
  linkUnlinkConfirmRef: React.RefObject<ConfirmModalRef | null>;
  onOpenActivateBatches?: () => void;
}

export const ProductTabs: React.FC<ProductTabsProps> = ({
  // Referencias
  productDataRef,

  // Estados del formulario
  nombre,
  setNombre,
  descripcion,
  setDescripcion,
  codigo,
  setCodigo,
  coste,
  setCoste,
  categoria,
  setCategoria,
  imagenPreview,
  setImagenPreview,
  imageFile,
  setImageFile,
  length,
  setLength,
  heigth,
  setHeigth,
  width,
  setWidth,
  weight,
  setWeight,
  weightUnit,
  setWeightUnit,
  dimensionUnit,
  setDimensionUnit,
  batchActive,
  setBatchActive,
  batchType,
  setBatchType,

  // Estados de datos externos
  categorias,
  loadingCategorias,
  stocks,
  enlaces,
  shops,
  initialData,

  // Funciones de manejo
  onAddStock,
  onEditStock,
  onCreateLink,
  onDeleteLink,

  // Utilidades
  productId,
  readOnly = false,
  linkUnlinkConfirmRef,
  onOpenActivateBatches,
}) => {
  const { t } = useTranslation('createEditProductsModule');
  const { canAccess, getTeamType } = usePermissions();
  const isEditMode = Boolean(productId);

  // 🎯 tenant_external puede gestionar enlaces (crear/eliminar) aunque el resto del formulario sea solo lectura
  const canManageLinks = !readOnly || getTeamType() === 'tenant_external';

  // 🎯 Verificar permiso para gestionar stock
  // products.manage-stock solo disponible para tenant y tenant_3pl, NO para tenant_external
  const canManageStock = canAccess(['tenant', 'tenant_3pl'], ['products.manage-stock']);
  const isTenant3pl = getTeamType() === 'tenant_3pl';
  /** Uso de lotes y tipo FIFO/LIFO/FEFO: solo editable por 3PL */
  const canEditBatchSettings = isTenant3pl && !readOnly;
  const canActivateBatches = canAccess(['tenant_3pl'], ['products.batches.activate']);
  const { data: hasStockMovements = false } = useQuery({
    queryKey: ['product-has-stock-movements', productId],
    enabled: Boolean(productId) && canActivateBatches,
    queryFn: async () => {
      const adapter = new ProductsApiAdapter(createHttpClient(apiClientWH));
      return adapter.hasStockMovements(productId!);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const hasProductStock = useMemo(() => {
    const fromLocations = stocks.some((row) => (row.stock ?? 0) > 0);
    const fromSummary = (initialData?.stock?.disponible ?? 0) > 0 || (initialData?.stock?.reservado ?? 0) > 0;
    return fromLocations || fromSummary;
  }, [stocks, initialData?.stock?.disponible, initialData?.stock?.reservado]);
  // tenant_external puede ver la pestaña Stock en modo solo lectura
  const canViewStockTab = canManageStock || readOnly;

  // Pestañas cuyo TabsContent está montado (evita defaultValue a tab inexistente → Radix/DOM removeChild)
  const availableTabValues = useMemo(() => {
    const s = new Set<string>(['datos']);
    if (canViewStockTab) s.add('stock');
    if (isEditMode && productId) s.add('batches');
    if (isEditMode) s.add('enlazar');
    return s;
  }, [isEditMode, canViewStockTab, productId]);

  const defaultTab = useMemo(() => {
    if (typeof window === 'undefined') return 'datos';
    const fromUrl = new URLSearchParams(window.location.search).get('tab') || 'datos';
    return availableTabValues.has(fromUrl) ? fromUrl : 'datos';
  }, [availableTabValues]);

  const tabsKey = isEditMode ? `edit-${productId ?? 'new'}` : 'create';

  const [activeTab, setActiveTab] = useState(() => defaultTab);

  // Al cambiar de producto o de query ?tab=, alinear con defaultTab
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [tabsKey, defaultTab]);

  // Si deja de existir la pestaña activa (permisos / datos), volver a datos
  useEffect(() => {
    if (!availableTabValues.has(activeTab)) {
      setActiveTab('datos');
    }
  }, [activeTab, availableTabValues]);

  return (
    <Tabs
      key={tabsKey}
      value={activeTab}
      onValueChange={(v) => {
        if (availableTabValues.has(v)) {
          setActiveTab(v);
        }
      }}
      className="p-6 h-auto"
    >
      {/* Tabs Navigation - Navegación de pestañas */}
      {isEditMode && (
        <TabsList>
          <TabsTrigger value="datos" data-testid="product-data-tab">
            {t('tabs.datos')}
          </TabsTrigger>
          {canViewStockTab && (
            <TabsTrigger value="stock" data-testid="product-stock-tab">
              {t('tabs.stock')}
            </TabsTrigger>
          )}
          <TabsTrigger value="batches" data-testid="product-batches-tab">
            {t('tabs.batches')}
          </TabsTrigger>
          <TabsTrigger value="enlazar" data-testid="product-links-tab">
            {t('tabs.enlazar')}
          </TabsTrigger>
        </TabsList>
      )}

      <ProductTabPanel value="datos" activeTab={activeTab} className="h-auto max-h-full">
        {loadingCategorias ? (
          <ProductDataSkeleton />
        ) : (
          <ProductData
            ref={productDataRef}
            initialData={initialData}
            readOnly={readOnly}
            nombre={nombre}
            setNombre={setNombre}
            descripcion={descripcion}
            setDescripcion={setDescripcion}
            codigo={codigo}
            setCodigo={setCodigo}
            coste={coste}
            setCoste={setCoste}
            categoria={categoria}
            setCategoria={setCategoria}
            imagenPreview={imagenPreview}
            setImagenPreview={setImagenPreview}
            imageFile={imageFile}
            setImageFile={setImageFile}
            length={length}
            setLength={setLength}
            heigth={heigth}
            setHeigth={setHeigth}
            width={width}
            setWidth={setWidth}
            weight={weight}
            setWeight={setWeight}
            categorias={categorias}
            loadingCategorias={loadingCategorias}
            weightUnit={weightUnit}
            setWeightUnit={setWeightUnit}
            dimensionUnit={dimensionUnit}
            setDimensionUnit={setDimensionUnit}
          />
        )}
      </ProductTabPanel>

      {canViewStockTab && (
        <ProductTabPanel value="stock" activeTab={activeTab} className="h-auto max-h-full">
          <Stock
            data={stocks}
            onAddStock={readOnly ? undefined : onAddStock}
            onEditStock={readOnly ? undefined : onEditStock}
            readOnly={readOnly}
            stockResumen={
              initialData && initialData.stock
                ? {
                    disponible: initialData.stock.disponible ?? 0,
                    reservado: initialData.stock.reservado ?? 0,
                  }
                : undefined
            }
            productId={productId}
            showLocationStock={isTenant3pl}
          />
        </ProductTabPanel>
      )}

      {isEditMode && productId && (
        <ProductTabPanel value="batches" activeTab={activeTab} className="h-auto max-h-full">
          <ProductBatches
            productId={productId}
            queryEnabled={activeTab === 'batches'}
            batchActive={batchActive}
            setBatchActive={setBatchActive}
            batchType={batchType}
            setBatchType={setBatchType}
            canEditBatchSettings={canEditBatchSettings}
            canActivateBatches={canActivateBatches}
            hasStockMovements={hasStockMovements}
            hasProductStock={hasProductStock}
            onOpenActivateBatches={onOpenActivateBatches}
          />
        </ProductTabPanel>
      )}

      {isEditMode && (
        <ProductTabPanel
          value="enlazar"
          activeTab={activeTab}
          className="h-auto max-h-full"
          data-testid="product-tab-panel-enlazar"
        >
          <Linked
            onCreateLink={onCreateLink}
            enlaces={enlaces}
            onDeleteLink={onDeleteLink}
            shops={shops}
            readOnly={!canManageLinks}
            confirmModalRef={linkUnlinkConfirmRef}
          />
        </ProductTabPanel>
      )}
    </Tabs>
  );
};
